import React, { useState } from "react";
import userPlaceholderImage from "../../assets/images/user.png";
import ToggleButton from "../../components/ToggleButton";
import MainModal from "../../components/MainModal";
import ChangeEmailModal from "../../components/ChangeEmailModal";
import EnterNewEmailModal from "../../components/EnterNewEmailModal";
import PrimaryButton from "../../components/PrimaryButton";
import ChangeNameModal from "../../components/ChangeNameModal";
import DeleteAccountModal from "../../components/DeleteAccountModal";
import { BoldPencilIcon, PhoneRingIcon, StarIcon, TrashIcon } from "../../assets/icons/Icons";
import { useAuth0 } from "@auth0/auth0-react";
import { LogOut } from 'lucide-react';
import { getConfig } from "../../config";

const AccountSettings = () => {
  const [showChangeEmail, setShowChangeEmail] = useState<boolean>(false);
  const [showEnterNewEmail, setShowEnterNewEmail] = useState<boolean>(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState<boolean>(false);
  const [showChangeName, setShowChangeName] = useState<boolean>(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState<boolean>(false);
  const { logout, user } = useAuth0();
  const { config } = getConfig();
  return (
    <>
      <div className="flex gap-2 flex-wrap">
        <div className="w-[380px]">
          <p className="text-[#9392A2] text-xs font-medium tracking-tight mb-3">General settings</p>
          <div className="w-full bg-white rounded-xl p-3 mb-2">
            <div className="flex items-center justify-between">
              <p className="text-[#9392A2] text-xs font-medium tracking-tight">Profile picture</p>
              <BoldPencilIcon />
            </div>
            <img className="w-14 h-14 mt-1 rounded-full" src={
              user?.picture ? user.picture : userPlaceholderImage
            } alt="profile" />
          </div>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center justify-between">
            <div>
              <p className="text-[#9392A2] text-xs font-medium tracking-tight">Name</p>
              <p className="text-neutral-900 text-sm font-medium tracking-tight">
                {/* put optional user values name || something else || somethinges || "Unknonw" */}
                {user?.nickname || user?.name || "Unknown"}
              </p>

            </div>
            <BoldPencilIcon onClick={() => setShowChangeName(true)} />
          </div>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center justify-between">
            <div>
              <p className="text-[#9392A2] text-xs font-medium tracking-tight">Email</p>
              <p className="text-neutral-900 text-sm font-medium tracking-tight">
                {user?.email}
                <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${user?.email_verified
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
                  }
                  ml-2
                  `
                }>
                  {user?.email_verified ? 'Verified' : 'Not Verified'}
                </span>
              </p>
            </div>
            <BoldPencilIcon onClick={() => setShowChangeEmail(true)} />
          </div>
        </div>
        <div className="w-[380px]">
          <p className="text-[#9392A2] text-xs font-medium tracking-tight mb-3">Preferences</p>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center justify-between">
            <div>
              <p className="text-[#9392A2] text-xs font-medium tracking-tight">Language</p>
              <p className="text-neutral-900 text-sm font-medium tracking-tight">English</p>
            </div>
            <BoldPencilIcon />
          </div>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center justify-between">
            <div>
              <p className="text-[#9392A2] text-xs font-medium tracking-tight">Notifications</p>
              <p className="text-neutral-900 text-sm font-medium tracking-tight">Allowed</p>
            </div>
            <ToggleButton isOn />
          </div>
        </div>
        <div className="w-[380px]">
          <p className="text-[#9392A2] text-xs font-medium tracking-tight mb-3">Other</p>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center gap-3">
            <StarIcon />
            <p className="text-neutral-900 text-sm font-medium tracking-tight">Rate CreativeSync</p>
          </div>
          <div className="w-full bg-white rounded-xl p-3 mb-2 flex items-center gap-3">
            <PhoneRingIcon />
            <p className="text-neutral-900 text-sm font-medium tracking-tight">Help center</p>
          </div>
          <div onClick={() => setShowDeleteAccount(true)} className="w-full bg-white rounded-xl p-3 mb-2 flex items-center gap-3 cursor-pointer">
            <TrashIcon stroke="#FF542E" fill="#FF542E" />
            <p className="text-[#FF542E] text-sm font-medium tracking-tight">Delete account</p>
          </div>
          <div
            onClick={() => logout({
              logoutParams: {
                returnTo: config.auth0.postLogoutRedirectUrl
              }
            })}

            className="w-full bg-white rounded-xl p-3 mb-2 flex items-center gap-3 cursor-pointer">
            <LogOut className="w-5 h-5 ml-1 text-[#FF542E]" />
            <p className="text-[#FF542E] text-sm font-medium tracking-tight">LogOut</p>
          </div>
        </div>
      </div>
      {
        showChangeEmail &&
        <MainModal
          label="Change email"
          setShowModal={setShowChangeEmail}
        >
          <ChangeEmailModal
            showNext={() => {
              setShowChangeEmail(false);
              setShowEnterNewEmail(true);
            }}
          />
        </MainModal>
      }
      {
        showEnterNewEmail &&
        <MainModal
          label="Enter new email"
          setShowModal={setShowEnterNewEmail}
        >
          <EnterNewEmailModal
            showPrev={() => {
              setShowEnterNewEmail(false);
              setShowChangeEmail(true);
            }}
            showNext={() => {
              setShowEnterNewEmail(false);
              setShowEmailConfirm(true);
            }}
          />
        </MainModal>
      }
      {
        showEmailConfirm &&
        <MainModal
          label="Email Confirmation"
          setShowModal={setShowEmailConfirm}
        >
          <div className="py-14">
            <p className="text-[#9392A2] text-sm font-medium leading-snug tracking-tight text-center">We’ve sent you email confirmation on <br /> <span className="text-[#8C84FF] font-semibold">mail@mail.com</span> Confirm and log in the <br /> system after.</p>
          </div>
          <PrimaryButton onClick={() => setShowEmailConfirm(false)} label="Open mail agent" className="py-2 w-full rounded-3xl" />
        </MainModal>
      }
      {
        showChangeName &&
        <MainModal
          label="Your name"
          setShowModal={setShowChangeName}
        >
          <ChangeNameModal />
        </MainModal>
      }
      {
        showDeleteAccount &&
        <MainModal
          label="Delete account"
          setShowModal={setShowDeleteAccount}
        >
          <DeleteAccountModal />
        </MainModal>
      }
    </>
  );
};

export default AccountSettings;